import { baseApi } from '@store/baseApi';

import type {
  BatchArchiveRequest,
  BatchArchiveResponse,
  CleanupApplyRequest,
  CleanupApplyResponse,
  PilotFixture,
} from '../types';

/**
 * Tag types owned by the appointment-types feature (its picker, management
 * modal, and org list all provide/consume them). The cleanup verbs mutate
 * the same encounter-type rows, so they must invalidate the same caches —
 * otherwise an archived type lingers in the picker until a hard refresh.
 */
const ENCOUNTER_TYPE_CACHE_TAGS = [
  'EncounterType',
  'EncounterTypesList',
  'AppointmentsList',
] as const;

type EncounterTypeCacheTag = {
  type: (typeof ENCOUNTER_TYPE_CACHE_TAGS)[number];
  id: string;
};

/** The list-level tags every appointment-types mutation invalidates. */
const typeListTags = (orgId: string): EncounterTypeCacheTag[] => [
  { type: 'EncounterTypesList', id: `ORG-${orgId}` },
  { type: 'EncounterTypesList', id: 'LIST' },
  { type: 'AppointmentsList', id: 'LIST' },
];

/**
 * Practice cleanup wizard API (GDN-2295 / GDN-2296 / GDN-2320).
 *
 * The bootstrap read returns the pilot-fixture row shape verbatim
 * (`PilotFixture`), so its response flows straight into `deriveWizardData`
 * with no adapter — the fixture format IS the API contract. Hand-injected
 * (no generated OpenAPI client) as the feature's own endpoints.
 *
 * Apply fans across three verbs: `batchArchive` (archives/restores/freed
 * templates, GDN-2294), `cleanupApply` (promote/merge, GDN-2320), and
 * `renameEncounterType` (set-aside PATCH renames). All three invalidate the
 * appointment-types caches so pickers and the management modal refresh
 * after a wizard apply or a catalog archive/restore. The bootstrap read
 * itself is deliberately untagged — the catalog owns its refresh with an
 * awaited refetch (fresh rows before the verb resolves).
 */
export const practiceCleanupApi = baseApi
  .enhanceEndpoints({ addTagTypes: ENCOUNTER_TYPE_CACHE_TAGS })
  .injectEndpoints({
    endpoints: (builder) => ({
      getCleanupBootstrap: builder.query<PilotFixture, { orgId: string }>({
        query: ({ orgId }) => ({
          url: `/v2/provider/organizations/${orgId}/encounter-types/cleanup-bootstrap`,
        }),
      }),
      batchArchiveEncounterTypes: builder.mutation<
        BatchArchiveResponse,
        { orgId: string; body: BatchArchiveRequest }
      >({
        query: ({ orgId, body }) => ({
          url: `/v2/provider/organizations/${orgId}/encounter-types/batch-archive`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { orgId, body }) => [
          ...typeListTags(orgId),
          ...[...body.archive_type_ids, ...body.restore_type_ids].map(
            (id): EncounterTypeCacheTag => ({ type: 'EncounterType', id })
          ),
        ],
      }),
      cleanupApply: builder.mutation<
        CleanupApplyResponse,
        { orgId: string; body: CleanupApplyRequest }
      >({
        query: ({ orgId, body }) => ({
          url: `/v2/provider/organizations/${orgId}/encounter-types/cleanup-apply`,
          method: 'POST',
          body,
        }),
        // Merges/promotions rescope, rename, and soft-delete types — the
        // touched-id set is wide (winners, losers, adopted defaults), so
        // list-level invalidation is the honest granularity.
        invalidatesTags: (_result, _error, { orgId }) => typeListTags(orgId),
      }),
      renameEncounterType: builder.mutation<
        unknown,
        { orgId: string; typeId: string; name: string }
      >({
        query: ({ orgId, typeId, name }) => ({
          url: `/v2/provider/organizations/${orgId}/encounter-types/${typeId}`,
          method: 'PATCH',
          body: { encounter_type: name },
        }),
        invalidatesTags: (_result, _error, { orgId, typeId }) => [
          ...typeListTags(orgId),
          { type: 'EncounterType', id: typeId },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetCleanupBootstrapQuery,
  useBatchArchiveEncounterTypesMutation,
  useCleanupApplyMutation,
  useRenameEncounterTypeMutation,
} = practiceCleanupApi;
